"use client";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, BadgeCheck, MessageCircle } from "lucide-react";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import {
  bundledExamNote,
  certificationFaqs,
  claudeExams,
  badgeNote,
  examFeeNote,
  partnerTierNote,
  retakeNote,
  trackGuidance,
  validityNote,
} from "@/content/academy-certification";
import { certificationCourses } from "@/content/academy-courses";
import { whatsappHref } from "@/lib/whatsapp";

// Claude certification hub. Read the header of content/academy-certification.ts
// before touching any fee or any claim about what we are — particularly the rules
// that we are not the certifying body and do not publish our own course fee.

export default function ClaudeCertification() {
  const locale = useLocale();
  const reduce = useReducedMotion();
  const inView = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-60px" },
        transition: { duration: 0.5 },
      };
  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP). Below-the-fold sections keep the inView fade.
  const heroIn = reduce
    ? {}
    : {
        initial: { y: 12 },
        animate: { y: 0 },
        transition: { duration: 0.5 },
      };
  const lp = (href: string) => getLocalizedPathname(href, locale);
  const register = lp("/academy/register-interest/");

  return (
    <main className="bg-background">
      <section className="pb-14 pt-24 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...heroIn} className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Anchor Academy")} · {t(locale, "Claude Certification")}
            </p>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-6xl">
              {t(locale, "Claude certification in Malaysia and Singapore — the four exams, and which one your team needs")}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-neutral-muted md:text-xl">
              {t(
                locale,
                "Anthropic runs four Claude certifications. We prepare people for three of them, the exam attempt is included in the course fee, and training delivered in Malaysia is claimable against your HRD Corp levy. Here is what each exam costs and who should sit it.",
              )}
              {/* In-text anchor to the "Claude partner Malaysia" owner page (2026-10-08). */}
              {" "}
              {t(locale, "We prepare candidates as a ")}
              <Link
                href={getLocalizedPathname("/partners/anthropic-claude-partner/", locale)}
                className="font-medium text-primary underline underline-offset-4"
              >
                {t(locale, "Claude partner in Malaysia")}
              </Link>
              {t(locale, " — Anthropic awards the certification, not us.")}
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <Link
                href={register}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
              >
                {t(locale, "Ask about a cohort")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <a
                href="#exams"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 px-8 py-3 text-sm font-semibold text-primary transition hover:border-primary hover:bg-primary/5"
              >
                {t(locale, "Compare the four exams")}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The four exams — the table the SERP is asking for */}
      <section id="exams" className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...heroIn} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "The four Claude certifications")}
          </motion.h2>
          <motion.div {...heroIn} className="mt-8 overflow-x-auto">
            <table className="w-full min-w-[46rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-3 pr-5 font-semibold text-neutral-emphasis">{t(locale, "Exam")}</th>
                  <th className="py-3 pr-5 font-semibold text-neutral-emphasis">{t(locale, "Code")}</th>
                  <th className="py-3 pr-5 font-semibold text-neutral-emphasis">{t(locale, "Format")}</th>
                  <th className="py-3 pr-5 font-semibold text-neutral-emphasis">{t(locale, "Exam fee")}</th>
                  <th className="py-3 pr-5 font-semibold text-neutral-emphasis">{t(locale, "Who it is for")}</th>
                  <th className="py-3 font-semibold text-neutral-emphasis">{t(locale, "We prep it")}</th>
                </tr>
              </thead>
              <tbody>
                {claudeExams.map((e) => (
                  <tr key={e.code} className="border-b border-border/60 align-top">
                    <td className="py-4 pr-5">
                      <span className="font-medium text-neutral-emphasis">{t(locale, e.name)}</span>
                      <span className="mt-1 block text-xs leading-relaxed text-neutral-muted">
                        {t(locale, e.covers)}
                      </span>
                    </td>
                    <td className="py-4 pr-5 font-mono text-xs text-neutral-muted">{e.code}</td>
                    <td className="py-4 pr-5 whitespace-nowrap text-neutral-muted">{t(locale, e.format)}</td>
                    <td className="py-4 pr-5 whitespace-nowrap font-semibold text-primary">{e.fee}</td>
                    <td className="py-4 pr-5 leading-relaxed text-neutral-muted">{t(locale, e.audience)}</td>
                    <td className="py-4">
                      {e.prepTier ? (
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary">
                          <BadgeCheck className="h-4 w-4" aria-hidden />
                          {e.prepTier}
                        </span>
                      ) : (
                        <span className="text-sm text-neutral-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
          <motion.p {...inView} className="mt-5 max-w-3xl text-sm leading-relaxed text-neutral-muted">
            {t(locale, examFeeNote)}
          </motion.p>
          <motion.p {...inView} className="mt-4 max-w-3xl rounded-lg border border-primary/30 bg-primary/5 p-5 text-sm leading-relaxed text-neutral-emphasis">
            {t(locale, bundledExamNote)}
          </motion.p>
          {/* The Architect blueprint. Published by Anthropic in the CCAR-F exam guide but almost
              never quoted locally, and it is the most useful thing on this page for a buyer:
              the weights double as an interview script for vetting any Claude partner. Sourced
              for CCAR-F only — the other three exams publish no blueprint we have verified. */}
          {(() => {
            const arch = claudeExams.find((e) => e.blueprint?.length);
            if (!arch?.blueprint) return null;
            return (
              <motion.div
                {...inView}
                className="mt-8 max-w-3xl rounded-lg border border-border bg-card p-6"
              >
                <h3 className="font-semibold text-neutral-emphasis">
                  {t(locale, "What the Architect exam actually weights")}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">
                  {t(
                    locale,
                    "Anthropic publishes a weighted blueprint for the Architect — Foundations exam. It is worth reading even if nobody on your team sits it: these are the areas a Claude architect is expected to defend, so they make a fair interview script when you are choosing a partner.",
                  )}
                </p>
                <ul className="mt-4 space-y-2">
                  {arch.blueprint.map((d) => (
                    <li key={d.domain} className="flex items-baseline justify-between gap-4 text-sm">
                      <span className="text-neutral-muted">{t(locale, d.domain)}</span>
                      <span className="font-mono font-semibold text-neutral-emphasis">{d.weight}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 border-t border-border pt-4 text-sm leading-relaxed text-neutral-muted">
                  {arch.scenarios ? `${t(locale, arch.scenarios)} · ` : ""}
                  {arch.passMark ? t(locale, arch.passMark) : ""}
                </p>
              </motion.div>
            );
          })()}
          <motion.div {...inView} className="mt-8 grid max-w-5xl gap-5 md:grid-cols-2">
            {[
              { h: "Valid for 12 months", body: validityNote },
              { h: "If someone does not pass", body: retakeNote },
              { h: "How the credential is issued", body: badgeNote },
              { h: "Certification and partner tier", body: partnerTierNote },
            ].map((b) => (
              <div key={b.h} className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-semibold text-neutral-emphasis">{t(locale, b.h)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, b.body)}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Which track for which role */}
      <section className="py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Which certification should each person take?")}
          </motion.h2>
          <div className="mt-8 space-y-4">
            {trackGuidance.map((g) => (
              <motion.div {...inView} key={g.role} className="rounded-lg border border-border bg-card p-6">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                  <h3 className="font-semibold text-neutral-emphasis">{t(locale, g.role)}</h3>
                  <span className="text-sm font-medium text-primary">{t(locale, g.take)}</span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, g.why)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our prep courses */}
      <section className="border-y border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Our certification prep courses")}
          </motion.h2>
          <motion.p {...inView} className="mt-3 max-w-3xl text-neutral-muted">
            {t(
              locale,
              "Run as corporate cohorts at your office or remotely, across Malaysia and Singapore. Course fees are quoted on enquiry against team size and scope.",
            )}
          </motion.p>
          <motion.p {...inView} className="mt-3 max-w-3xl text-neutral-muted">
            {t(
              locale,
              "The courses are taught by our team, which includes 10 Claude Certified Architects (CCAR-F) who design and build Claude agents in production.",
            )}
          </motion.p>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {certificationCourses.map((c) => (
              <motion.div {...inView} key={c.tier} className="rounded-lg border border-border bg-card p-6">
                <span className="font-display text-sm font-bold text-primary">{c.tier}</span>
                <h3 className="mt-2 font-semibold text-neutral-emphasis">{t(locale, c.title)}</h3>
                <p className="mt-1 text-xs font-medium text-neutral-muted">{t(locale, c.who)}</p>
                <p className="mt-3 text-sm leading-relaxed text-neutral-muted">{t(locale, c.body)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <StructuredData type="faq" faqs={certificationFaqs} />
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {t(locale, "Claude certification — common questions")}
          </motion.h2>
          <div className="mt-8 space-y-6">
            {certificationFaqs.map((f) => (
              <motion.div {...inView} key={f.question} className="rounded-lg border border-border bg-card p-6">
                <h3 className="font-semibold text-neutral-emphasis">{t(locale, f.question)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, f.answer)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-neutral-surface py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-bold text-neutral-emphasis">
            {t(locale, "Tell us the roles and we will map the tracks")}
          </motion.h2>
          <motion.p {...inView} className="mx-auto mt-4 max-w-2xl text-neutral-muted">
            {t(
              locale,
              "Team size, the roles on it and which exams you are targeting is enough. We come back with a scoped programme and a quotation — and we will say so if a cheaper track gets you the same result.",
            )}
          </motion.p>
          <motion.div {...inView} className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href={register}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {t(locale, "Ask about a cohort")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <a
              href={whatsappHref("Hi Anchor Sprint, I'd like to ask about Claude certification prep (from your Claude Certification page).")}
              target="_blank"
              rel="noopener noreferrer"
              data-track-event="contact_whatsapp_click"
              data-track-location="claude_certification_cta"
              data-track-phone="601159246128"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              <MessageCircle className="h-4 w-4" aria-hidden />
              {t(locale, "WhatsApp us")}
            </a>
          </motion.div>
          <motion.p {...inView} className="mt-8 text-sm text-neutral-muted">
            <Link href={lp("/academy/")} className="text-primary hover:underline">
              {t(locale, "Anchor Academy")}
            </Link>
            {" · "}
            <Link href={lp("/services/training-education/")} className="text-primary hover:underline">
              {t(locale, "HRD Corp claimable AI training")}
            </Link>
            {" · "}
            <Link href={lp("/singapore/claude-training/")} className="text-primary hover:underline">
              {t(locale, "Claude training in Singapore")}
            </Link>
          </motion.p>
        </div>
      </section>
    </main>
  );
}
